import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiCalendar, FiUsers, FiStar, FiCheck, FiMessageCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Booking = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [bookingData, setBookingData] = useState({
    checkIn: '',
    checkOut: '',
    guests: 2,
    room: 'mountain-view'
  });

  const benefits = [
    {
      icon: FiStar,
      title: 'Best Rate Guarantee',
      description: '15% lower than OTA prices'
    },
    {
      icon: FiCheck,
      title: 'Free Driver Care',
      description: 'Accommodation & meals included'
    },
    {
      icon: FiMessageCircle,
      title: '24/7 WhatsApp Support',
      description: 'Direct line to our team'
    }
  ];

  const rooms = [
    { id: 'mountain-view', name: 'Mountain View Suite', price: 8500 },
    { id: 'garden-sanctuary', name: 'Garden Sanctuary', price: 7000 },
    { id: 'riverside-retreat', name: 'Riverside Retreat', price: 6500 }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      country: 'Australia',
      rating: 5,
      comment: 'The closest authentic experience to Sinharaja. The waterfall access is incredible!',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'
    },
    {
      name: 'Raj Patel',
      country: 'UK',
      rating: 5,
      comment: 'Perfect for birdwatching. Saw 12 endemic species from the garden alone.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'
    },
    {
      name: 'Emma Chen',
      country: 'Singapore',
      rating: 5,
      comment: 'The driver accommodation made our trip seamless. Highly recommend booking direct!',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'
    }
  ];

  const handleInputChange = (field, value) => {
    setBookingData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateTotal = () => {
    const selectedRoom = rooms.find(room => room.id === bookingData.room);
    if (!selectedRoom || !bookingData.checkIn || !bookingData.checkOut) return 0;
    
    const checkIn = new Date(bookingData.checkIn);
    const checkOut = new Date(bookingData.checkOut);
    const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
    
    return nights > 0 ? selectedRoom.price * nights : 0;
  };

  return (
    <section id="booking" className="py-20 relative overflow-hidden">
      <div className="container mx-auto px-6" ref={ref}>
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-white mb-6 font-['Playfair_Display']"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            Book <span className="text-gradient">Direct & Save</span>
          </motion.h2>
          <motion.p
            className="text-xl text-gray-300 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
          >
            Skip the commissions and enjoy exclusive benefits available only through direct booking
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Booking Form */}
          <motion.div
            className="glass-card p-8"
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.6 }}
          >
            <h3 className="text-2xl font-bold text-white mb-6 font-['Playfair_Display']">
              Reserve Your Sanctuary
            </h3>
            
            <div className="space-y-6">
              {/* Date Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Check-in
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={bookingData.checkIn}
                      onChange={(e) => handleInputChange('checkIn', e.target.value)}
                      className="w-full px-4 py-3 glass-morphism text-white rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      min={new Date().toISOString().split('T')[0]}
                    />
                    <SafeIcon icon={FiCalendar} className="absolute right-3 top-3 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Check-out
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={bookingData.checkOut}
                      onChange={(e) => handleInputChange('checkOut', e.target.value)}
                      className="w-full px-4 py-3 glass-morphism text-white rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      min={bookingData.checkIn || new Date().toISOString().split('T')[0]}
                    />
                    <SafeIcon icon={FiCalendar} className="absolute right-3 top-3 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Guests */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Guests
                </label>
                <div className="relative">
                  <select
                    value={bookingData.guests}
                    onChange={(e) => handleInputChange('guests', parseInt(e.target.value))}
                    className="w-full px-4 py-3 glass-morphism text-white rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-blue-400 appearance-none"
                  >
                    {[1,2,3,4,5,6].map(num => (
                      <option key={num} value={num} className="bg-gray-800">
                        {num} {num === 1 ? 'Guest' : 'Guests'}
                      </option>
                    ))}
                  </select>
                  <SafeIcon icon={FiUsers} className="absolute right-3 top-3 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Room Selection */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Room Type
                </label>
                <div className="space-y-3">
                  {rooms.map((room) => (
                    <motion.div
                      key={room.id}
                      className={`glass-morphism p-4 rounded-lg cursor-pointer transition-all ${
                        bookingData.room === room.id ? 'ring-2 ring-blue-400' : ''
                      }`}
                      onClick={() => handleInputChange('room', room.id)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-white font-medium">{room.name}</h4>
                          <p className="text-gray-300 text-sm">LKR {room.price.toLocaleString()}/night</p>
                        </div>
                        <div className={`w-4 h-4 rounded-full border-2 ${
                          bookingData.room === room.id 
                            ? 'bg-blue-400 border-blue-400' 
                            : 'border-gray-400'
                        }`} />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Total */}
              {calculateTotal() > 0 && (
                <motion.div
                  className="glass-morphism p-4 rounded-lg"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex justify-between items-center text-white">
                    <span className="text-lg font-semibold">Total:</span>
                    <span className="text-2xl font-bold">
                      LKR {calculateTotal().toLocaleString()}
                    </span>
                  </div>
                  <p className="text-green-300 text-sm mt-1">
                    15% savings compared to OTA prices
                  </p>
                </motion.div>
              )}

              {/* CTA Buttons */}
              <div className="space-y-3">
                <motion.button
                  className="w-full py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-lg hover-glow"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Check Availability
                </motion.button>
                
                <motion.button
                  className="w-full py-3 glass-morphism text-white font-medium rounded-lg hover-glow flex items-center justify-center space-x-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SafeIcon icon={FiMessageCircle} className="w-5 h-5" />
                  <span>WhatsApp Us</span>
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* Benefits & Testimonials */}
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.8 }}
          >
            {/* Direct Booking Benefits */}
            <div className="glass-card p-6">
              <h3 className="text-xl font-bold text-white mb-4 font-['Playfair_Display']">
                Direct Booking Benefits
              </h3>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit.title}
                    className="flex items-start space-x-3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 1.0 + index * 0.1 }}
                  >
                    <div className="w-10 h-10 glass-morphism rounded-full flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={benefit.icon} className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold">{benefit.title}</h4>
                      <p className="text-gray-300 text-sm">{benefit.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div className="glass-card p-6">
              <h3 className="text-xl font-bold text-white mb-4 font-['Playfair_Display']">
                Guest Reviews
              </h3>
              <div className="space-y-4">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={testimonial.name}
                    className="glass-morphism p-4 rounded-lg"
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 1.3 + index * 0.2 }}
                  >
                    <div className="flex items-start space-x-3">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="text-white font-medium">{testimonial.name}</h4>
                          <span className="text-gray-400 text-sm">• {testimonial.country}</span>
                        </div>
                        <div className="flex items-center space-x-1 mb-2">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <SafeIcon key={i} icon={FiStar} className="w-4 h-4 text-yellow-400 fill-current" />
                          ))}
                        </div>
                        <p className="text-gray-300 text-sm italic">"{testimonial.comment}"</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Serenity Club CTA */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.6 }}
        >
          <div className="glass-card p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4 font-['Playfair_Display']">
              Join the Serenity Club
            </h3>
            <p className="text-gray-300 mb-6">
              Get exclusive access to member-only rates, early booking privileges, 
              and special rainforest experiences.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 glass-morphism text-white placeholder-gray-400 rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <motion.button
                className="px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-lg hover-glow"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get 10% Off
              </motion.button>
            </div>
            <p className="text-gray-400 text-xs mt-3">
              No spam. Unsubscribe anytime. Member rates are 10% lower than public prices.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Booking;